/* eslint-disable */
import * as React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
  HStack,
  Divider,
  Collapse,
  Tooltip,
} from '@chakra-ui/react';
import { getSessionCandidateId } from 'helpers/session';
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from '@choc-ui/chakra-autocomplete';
import {
  ColumnFiltersState,
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useJwt } from 'react-jwt';
import { getCookie } from 'cookies-next';
import {
  MdCancel,
  MdChevronLeft,
  MdChevronRight,
  MdDoneAll,
  MdFilterList,
  MdInfo,
  MdLoupe,
  MdPersonAdd,
  MdPhone,
  MdRefresh,
  MdThumbUp,
  MdUpload,
} from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Invoice from './orderDetails';

// ── Types ────────────────────────────────────────────────────────────────────

type RowObj = {
  Nombre: string;
  id: number;
  idSeccion: string;
  CURP: string;
  INE: string;
  ApellidoPaterno: string;
  ApellidoMaterno: string;
  Fotografia: string;
  Telefono: string;
  Voto: string;
  Genero: string;
  createdAt: string;
  Nacimiento: string;
  LogoPartido: string;
};

type VotoFilter = 'Todos' | 'Confirmado' | 'Positivo' | 'Negativo';

// ── Helpers ───────────────────────────────────────────────────────────────────

const votoBadge = (voto: string) => {
  if (voto === 'Confirmado') return { colorScheme: 'green', label: 'Confirmado' };
  if (voto === 'Positivo')   return { colorScheme: 'yellow', label: 'Positivo' };
  return { colorScheme: 'red', label: 'Negativo' };
};

const VOTO_FILTERS: { label: string; value: VotoFilter; color: string }[] = [
  { label: 'Todos',      value: 'Todos',      color: 'gray'   },
  { label: 'Confirmado', value: 'Confirmado', color: 'green'  },
  { label: 'Positivo',   value: 'Positivo',   color: 'yellow' },
  { label: 'Negativo',   value: 'Negativo',   color: 'red'    },
];

const inputStyle = {
  variant: 'filled' as const,
  borderRadius: 'lg',
  _focus: { bg: 'white', borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' },
};

// ── Component ────────────────────────────────────────────────────────────────

export default function SearchTableOrders(props: { tableData: any }) {
  const { tableData } = props;
  const toast = useToast();
  const cardBg   = useColorModeValue('white', 'navy.800');
  const filterBg = useColorModeValue('gray.50', 'navy.900');

  // ── Modals ──
  const { isOpen, onOpen, onClose }                             = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenImg,   onOpen: onOpenImg,   onClose: onCloseImg   } = useDisclosure();
  const [orden, setOrden] = React.useState(0);
  const [selectedVotante, setSelectedVotante] = React.useState<RowObj | null>(null);

  // ── Filter panel ──
  const [showFilters, setShowFilters] = React.useState(false);
  const [votoFilter,  setVotoFilter]  = React.useState<VotoFilter>('Todos');
  const [generoFilter, setGeneroFilter] = React.useState('');
  const [seccionFilter, setSeccionFilter] = React.useState('');
  const [fechaDesde, setFechaDesde] = React.useState('');
  const [fechaHasta, setFechaHasta] = React.useState('');
  const [nacDesde,   setNacDesde]   = React.useState('');
  const [nacHasta,   setNacHasta]   = React.useState('');

  // active filter count badge
  const activeFilters = [
    votoFilter !== 'Todos',
    !!generoFilter,
    !!seccionFilter,
    !!(fechaDesde || fechaHasta),
    !!(nacDesde   || nacHasta),
  ].filter(Boolean).length;

  const resetFilters = () => {
    setVotoFilter('Todos');
    setGeneroFilter('');
    setSeccionFilter('');
    setFechaDesde('');
    setFechaHasta('');
    setNacDesde('');
    setNacHasta('');
  };

  // ── Table state ──
  const textColor   = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const brandColor  = useColorModeValue('brand.500', 'brand.400');
  const rowHover    = useColorModeValue('gray.50', 'whiteAlpha.50');
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [data, setData] = React.useState<RowObj[]>(() => [...tableData]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 8,
  });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  // ── Derived: filtered data ──
  const filteredData = React.useMemo(() => {
    return data.filter((row) => {
      if (votoFilter !== 'Todos' && row.Voto !== votoFilter) return false;
      if (generoFilter && row.Genero !== generoFilter) return false;
      if (seccionFilter && String(row.idSeccion) !== seccionFilter) return false;

      if (fechaDesde || fechaHasta) {
        const created = row.createdAt ? new Date(row.createdAt) : null;
        if (!created) return false;
        if (fechaDesde && created < new Date(fechaDesde)) return false;
        if (fechaHasta && created > new Date(fechaHasta + 'T23:59:59')) return false;
      }

      if (nacDesde || nacHasta) {
        const nac = row.Nacimiento ? new Date(row.Nacimiento) : null;
        if (!nac) return false;
        if (nacDesde && nac < new Date(nacDesde)) return false;
        if (nacHasta && nac > new Date(nacHasta + 'T23:59:59')) return false;
      }

      return true;
    });
  }, [data, votoFilter, generoFilter, seccionFilter, fechaDesde, fechaHasta, nacDesde, nacHasta]);

  // unique sections for filter dropdown
  const uniqueSecciones = React.useMemo(() => {
    const s = new Set(data.map((r) => String(r.idSeccion)).filter(Boolean));
    return Array.from(s).sort();
  }, [data]);

  // ── Nuevo Votante form ──
  const token = getCookie('token');
  const jwt   = useJwt(token as string);
  const inputFileRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [textFileEvidencia, setTextFileEvidencia] = React.useState('');
  const [candi, setCandi] = React.useState('');
  const [secciones, setSecciones] = React.useState<any[]>([]);
  const generoOpts = [
    { id: '1', valor: 'Femenino'    },
    { id: '2', valor: 'Masculino'   },
    { id: '3', valor: 'Sin Definir' },
  ];

  const { handleSubmit, register, reset, formState: { isSubmitting } } = useForm();

  React.useEffect(() => {
    fetch(process.env.REACT_APP_URL_SERVER + '/secciones', { credentials: 'include' })
      .then((r) => r.json())
      .then(setSecciones)
      .catch(() => {});
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setTextFileEvidencia(f.name); }
  };

  const refreshTable = async () => {
    const candidateId = getSessionCandidateId();
    const endpoint = new URL(`${process.env.REACT_APP_URL_SERVER}/votantes`);
    if (candidateId) {
      endpoint.searchParams.set('idCandidato', String(candidateId));
    }

    const updated = await fetch(endpoint.toString(), { credentials: 'include' })
      .then((r) => r.json())
      .catch(() => null);
    if (updated) setData(updated);
  };

  const onSubmitNuevo = async (values: any) => {
    const vals = JSON.parse(JSON.stringify(jwt.decodedToken ?? '{}'));
    const formData = new FormData();
    formData.append('iduser', vals?.usuarioid ?? '');
    if (candi) {
      const parsed = JSON.parse(candi);
      formData.append('Seccion', parsed.value);
      formData.append('SeccionLabel', parsed.label);
    }
    for (const key in values) formData.append(key, values[key]);
    if (file) formData.append('file', file);

    const res = await fetch(process.env.REACT_APP_URL_SERVER + '/new_votante', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    if (res.ok) {
      toast({ title: 'Votante registrado', status: 'success', duration: 3000 });
      reset(); setFile(null); setTextFileEvidencia(''); setCandi('');
      onCloseNuevo();
      await refreshTable();
    } else {
      toast({ title: 'Error al guardar', status: 'error', duration: 3000 });
    }
  };

  // ── Columns ──────────────────────────────────────────────────────────────
  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    columnHelper.accessor('INE', {
      id: 'avatar',
      header: () => <Flex />,
      cell: (info) => (
        <Avatar size="md" src={info.getValue()} name={info.row.original.Nombre} />
      ),
    }),
    columnHelper.accessor('Nombre', {
      id: 'nombre',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">VOTANTE</Text>
      ),
      cell: (info) => {
        const r = info.row.original;
        return (
          <Box>
            <Text color={textColor} fontWeight="600" fontSize="sm">
              {r.Nombre} {r.ApellidoPaterno} {r.ApellidoMaterno}
            </Text>
            <Text color="gray.400" fontSize="xs">{r.CURP}</Text>
          </Box>
        );
      },
    }),
    columnHelper.accessor('idSeccion', {
      id: 'seccion',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">SECCIÓN</Text>
      ),
      cell: (info) => (
        <Badge colorScheme="purple" fontSize="xs" borderRadius="md" px="2">{info.getValue()}</Badge>
      ),
    }),
    columnHelper.accessor('Telefono', {
      id: 'telefono',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">TELÉFONO</Text>
      ),
      cell: (info) => (
        <Flex align="center" gap="6px">
          <Icon as={MdPhone} h="14px" w="14px" color="gray.400" />
          <Text color={textColor} fontSize="sm">
            <a href={'tel:' + info.getValue()}>{info.getValue()}</a>
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'registro',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">REGISTRO</Text>
      ),
      cell: (info) => {
        const val = info.getValue();
        if (!val) return <Text color="gray.400" fontSize="xs">—</Text>;
        return (
          <Text color="gray.500" fontSize="xs">
            {new Date(val).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
          </Text>
        );
      },
    }),
    columnHelper.accessor('Voto', {
      id: 'voto',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ESTATUS</Text>
      ),
      cell: (info) => {
        const { colorScheme, label } = votoBadge(String(info.getValue()));
        return (
          <Badge colorScheme={colorScheme} fontSize="xs" borderRadius="full" px="3" py="1">
            {label}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ACCIONES</Text>
      ),
      cell: (info) => (
        <HStack spacing="2px">
          <Tooltip label="Confirmar voto" placement="top">
            <IconButton aria-label="Confirmar" size="sm" variant="ghost" colorScheme="green"
              icon={<Icon as={MdDoneAll} h="18px" w="18px" />}
              onClick={async () => {
                const fd = new FormData();
                fd.append('id', info.getValue().toString());
                fd.append('Voto', 'Confirmado');
                await fetch(process.env.REACT_APP_URL_SERVER + '/votantes_mod', { method: 'POST', body: fd, credentials: 'include' });
                toast({ title: 'Voto Confirmado', status: 'success', duration: 2000 });
                await refreshTable();
              }}
            />
          </Tooltip>
          <Tooltip label="Marcar Positivo" placement="top">
            <IconButton aria-label="Positivo" size="sm" variant="ghost" colorScheme="yellow"
              icon={<Icon as={MdThumbUp} h="18px" w="18px" />}
              onClick={async () => {
                const fd = new FormData();
                fd.append('id', info.getValue().toString());
                fd.append('Voto', 'Positivo');
                await fetch(process.env.REACT_APP_URL_SERVER + '/votantes_mod', { method: 'POST', body: fd, credentials: 'include' });
                toast({ title: 'Marcado Positivo', status: 'info', duration: 2000 });
                await refreshTable();
              }}
            />
          </Tooltip>
          <Tooltip label="Marcar Negativo" placement="top">
            <IconButton aria-label="Negativo" size="sm" variant="ghost" colorScheme="red"
              icon={<Icon as={MdCancel} h="18px" w="18px" />}
              onClick={async () => {
                const fd = new FormData();
                fd.append('id', info.getValue().toString());
                fd.append('Voto', 'Negativo');
                await fetch(process.env.REACT_APP_URL_SERVER + '/votantes_mod', { method: 'POST', body: fd, credentials: 'include' });
                toast({ title: 'Marcado Negativo', status: 'warning', duration: 2000 });
                await refreshTable();
              }}
            />
          </Tooltip>
          <Tooltip label="Ver detalle" placement="top">
            <IconButton aria-label="Detalle" size="sm" variant="ghost" colorScheme="gray"
              icon={<Icon as={MdLoupe} h="18px" w="18px" />}
              onClick={() => { setOrden(info.getValue()); setSelectedVotante(info.row.original); onOpen(); }}
            />
          </Tooltip>
        </HStack>
      ),
    }),
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { columnFilters, globalFilter, pagination },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const createPages = (count: number) => Array.from({ length: count }, (_, i) => i + 1);

  // summary counts
  const counts = React.useMemo(() => ({
    total:      data.length,
    confirmado: data.filter((r) => r.Voto === 'Confirmado').length,
    positivo:   data.filter((r) => r.Voto === 'Positivo').length,
    negativo:   data.filter((r) => r.Voto === 'Negativo').length,
  }), [data]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Flex direction="column" w="100%" overflowX={{ sm: 'scroll', lg: 'hidden' }}>

      {/* ── Toolbar ───────────────────────────────────────── */}
      <Flex align="center" justify="space-between" w="100%" px="22px" mb="16px" flexWrap="wrap" gap="10px">
        <DebouncedInput
          value={globalFilter ?? ''}
          onChange={(v) => setGlobalFilter(String(v))}
          placeholder="Busca por nombre, CURP..."
        />
        <HStack spacing="8px">
          <Tooltip label="Actualizar tabla">
            <IconButton
              aria-label="Refresh"
              size="md"
              variant="outline"
              icon={<Icon as={MdRefresh} h="18px" w="18px" />}
              onClick={refreshTable}
            />
          </Tooltip>
          <Button
            variant="outline"
            fontSize="sm"
            fontWeight="500"
            leftIcon={<Icon as={MdFilterList} />}
            onClick={() => setShowFilters((v) => !v)}
            position="relative"
          >
            Filtros
            {activeFilters > 0 && (
              <Badge
                colorScheme="brand"
                borderRadius="full"
                position="absolute"
                top="-6px"
                right="-6px"
                fontSize="10px"
                minW="18px"
                h="18px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {activeFilters}
              </Badge>
            )}
          </Button>
          <Button variant="brand" fontSize="sm" fontWeight="500" onClick={onOpenNuevo}>
            + Nuevo Votante
          </Button>
        </HStack>
      </Flex>

      {/* ── Summary chips ──────────────────────────────────── */}
      <Flex px="22px" mb="12px" gap="8px" flexWrap="wrap">
        {VOTO_FILTERS.map((f) => {
          const count = f.value === 'Todos' ? counts.total
            : f.value === 'Confirmado' ? counts.confirmado
            : f.value === 'Positivo'   ? counts.positivo
            : counts.negativo;
          const isActive = votoFilter === f.value;
          return (
            <Badge
              key={f.value}
              colorScheme={isActive ? f.color : 'gray'}
              variant={isActive ? 'solid' : 'outline'}
              fontSize="xs"
              px="3"
              py="1"
              borderRadius="full"
              cursor="pointer"
              onClick={() => { setVotoFilter(f.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              _hover={{ opacity: 0.8 }}
              userSelect="none"
            >
              {f.label} ({count})
            </Badge>
          );
        })}
        {activeFilters > 0 && (
          <Badge
            colorScheme="orange"
            variant="outline"
            fontSize="xs"
            px="3"
            py="1"
            borderRadius="full"
            cursor="pointer"
            onClick={resetFilters}
            _hover={{ opacity: 0.8 }}
            userSelect="none"
          >
            ✕ Limpiar filtros
          </Badge>
        )}
      </Flex>

      {/* ── Filter panel (collapse) ────────────────────────── */}
      <Collapse in={showFilters} animateOpacity>
        <Box
          mx="22px"
          mb="16px"
          p="16px"
          bg={filterBg}
          borderRadius="12px"
          border="1px solid"
          borderColor={borderColor}
        >
          <Text fontSize="sm" fontWeight="700" color={textColor} mb="14px">
            Filtros avanzados
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="16px">

            {/* Género */}
            <Box>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" mb="6px">
                GÉNERO
              </FormLabel>
              <Select
                size="sm"
                borderRadius="8px"
                value={generoFilter}
                onChange={(e) => { setGeneroFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              >
                <option value="">Todos</option>
                <option value="1">Femenino</option>
                <option value="2">Masculino</option>
                <option value="3">Sin Definir</option>
              </Select>
            </Box>

            {/* Sección */}
            <Box>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" mb="6px">
                SECCIÓN
              </FormLabel>
              <Select
                size="sm"
                borderRadius="8px"
                value={seccionFilter}
                onChange={(e) => { setSeccionFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              >
                <option value="">Todas</option>
                {uniqueSecciones.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </Box>

            {/* Fecha de registro */}
            <Box>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" mb="6px">
                FECHA DE REGISTRO
              </FormLabel>
              <HStack spacing="8px">
                <Input
                  size="sm"
                  type="date"
                  borderRadius="8px"
                  value={fechaDesde}
                  onChange={(e) => { setFechaDesde(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
                />
                <Text fontSize="xs" color="gray.400">al</Text>
                <Input
                  size="sm"
                  type="date"
                  borderRadius="8px"
                  value={fechaHasta}
                  onChange={(e) => { setFechaHasta(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
                />
              </HStack>
            </Box>

            {/* Fecha de nacimiento */}
            <Box>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.500" mb="6px">
                FECHA DE NACIMIENTO
              </FormLabel>
              <HStack spacing="8px">
                <Input
                  size="sm"
                  type="date"
                  borderRadius="8px"
                  value={nacDesde}
                  onChange={(e) => { setNacDesde(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
                />
                <Text fontSize="xs" color="gray.400">al</Text>
                <Input
                  size="sm"
                  type="date"
                  borderRadius="8px"
                  value={nacHasta}
                  onChange={(e) => { setNacHasta(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
                />
              </HStack>
            </Box>

          </SimpleGrid>

          <Flex mt="14px" justify="flex-end">
            <Button size="sm" variant="ghost" colorScheme="red" onClick={resetFilters}>
              Limpiar todos los filtros
            </Button>
          </Flex>
        </Box>
      </Collapse>

      {/* ── Filtered result count ──────────────────────────── */}
      {activeFilters > 0 && (
        <Text px="22px" mb="8px" fontSize="xs" color="gray.500">
          Mostrando <strong>{filteredData.length}</strong> de {data.length} registros con los filtros activos
        </Text>
      )}

      {/* ── Detail Modal ───────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="brand.500" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <HStack gap={3}>
                <Icon as={MdInfo} color="white" w={5} h={5} />
                <Text color="white" fontWeight="700" fontSize="lg">Detalle #{orden}</Text>
              </HStack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <ModalBody px={6} py={5}>
            {selectedVotante && (
              <>
                {/* Avatar + nombre + voto */}
                <Flex align="center" gap={4} mb={5}>
                  <Avatar size="xl" src={selectedVotante.INE} name={`${selectedVotante.Nombre} ${selectedVotante.ApellidoPaterno}`} />
                  <Box>
                    <Text fontWeight="700" fontSize="lg" color={textColor}>
                      {selectedVotante.Nombre} {selectedVotante.ApellidoPaterno} {selectedVotante.ApellidoMaterno}
                    </Text>
                    <Text color="gray.400" fontSize="sm" mt="2px">{(selectedVotante as any).CURP || '—'}</Text>
                    <Badge mt={2} colorScheme={votoBadge(selectedVotante.Voto).colorScheme} borderRadius="full" px={3}>
                      {votoBadge(selectedVotante.Voto).label}
                    </Badge>
                  </Box>
                </Flex>

                <Divider mb={4} />

                {/* Datos personales */}
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.400" mb={3}>
                  Datos Personales
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={5}>
                  <DetailField label="Teléfono" value={selectedVotante.Telefono || '—'} />
                  <DetailField label="Email" value={(selectedVotante as any).Email || '—'} />
                  <DetailField label="Sección Electoral" value={String(selectedVotante.idSeccion || '—')} />
                  <DetailField label="Género" value={
                    selectedVotante.Genero === '1' ? 'Femenino'
                    : selectedVotante.Genero === '2' ? 'Masculino'
                    : 'Sin Definir'
                  } />
                  <DetailField label="Fecha de Nacimiento" value={
                    selectedVotante.Nacimiento
                      ? new Date(selectedVotante.Nacimiento).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
                      : '—'
                  } />
                  <DetailField label="Ocupación" value={(selectedVotante as any).Ocupacion || '—'} />
                  <DetailField label="Fecha de Registro" value={
                    selectedVotante.createdAt
                      ? new Date(selectedVotante.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'
                  } />
                </SimpleGrid>

                <Divider mb={4} />

                {/* Domicilio */}
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.400" mb={3}>
                  Domicilio
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={5}>
                  <DetailField label="Calle" value={(selectedVotante as any).Calle || '—'} />
                  <DetailField label="Número de Vivienda" value={(selectedVotante as any).NVivienda || '—'} />
                  <DetailField label="Colonia" value={(selectedVotante as any).Colonia || '—'} />
                  <DetailField label="Código Postal" value={(selectedVotante as any).CodigoPostal || '—'} />
                  <DetailField label="Referencias" value={(selectedVotante as any).Referencias || '—'} />
                </SimpleGrid>

                {/* Fotografía INE */}
                {selectedVotante.INE && (
                  <>
                    <Divider mb={4} />
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.400" mb={3}>
                      Fotografía / INE
                    </Text>
                    <Box
                      as="img"
                      src={selectedVotante.INE}
                      alt="INE"
                      borderRadius="xl"
                      maxH="220px"
                      objectFit="cover"
                      cursor="zoom-in"
                      _hover={{ opacity: 0.85, transform: 'scale(1.01)' }}
                      transition="all 0.2s"
                      onClick={onOpenImg}
                    />
                    <Text fontSize="xs" color="gray.400" mt={1}>Click para ampliar</Text>
                  </>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3} px={6} py={4}>
            <Button variant="ghost" borderRadius="xl" onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Image Lightbox ─────────────────────────────────── */}
      <Modal isOpen={isOpenImg} onClose={onCloseImg} size="4xl" isCentered motionPreset="scale">
        <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.800" />
        <ModalContent bg="transparent" shadow="none" mx={4}>
          <ModalCloseButton color="white" size="lg" zIndex={10} top={2} right={2} bg="blackAlpha.500" borderRadius="full" />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
            <Box
              as="img"
              src={selectedVotante?.INE}
              alt="INE ampliado"
              borderRadius="2xl"
              maxH="85vh"
              maxW="100%"
              objectFit="contain"
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ── Nuevo Votante Modal ────────────────────────────── */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="4xl" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="brand.500" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <HStack gap={3}>
                <Icon as={MdPersonAdd} color="white" w={5} h={5} />
                <Text color="white" fontWeight="700" fontSize="lg">Registrar Nuevo Votante</Text>
              </HStack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <form onSubmit={handleSubmit(onSubmitNuevo)}>
            <ModalBody px={6} py={5}>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                <Stack gap="16px">
                  <FField label="Nombre">
                    <Input placeholder="Emiliano" {...inputStyle} {...register('Nombre', { required: true })} />
                  </FField>
                  <FField label="Apellido Paterno">
                    <Input placeholder="Torres" {...inputStyle} {...register('ApellidoPaterno', { required: true })} />
                  </FField>
                  <FField label="Apellido Materno">
                    <Input placeholder="García" {...inputStyle} {...register('ApellidoMaterno', { required: true })} />
                  </FField>
                  <FField label="Teléfono">
                    <Input type="number" placeholder="3312345678" {...inputStyle} {...register('Telefono', { required: true })} />
                  </FField>
                  <FField label="Email">
                    <Input type="email" placeholder="correo@ejemplo.com" {...inputStyle} {...register('Email')} />
                  </FField>
                  <FField label="Ocupación">
                    <Input placeholder="Empleado" {...inputStyle} {...register('Ocupacion')} />
                  </FField>
                  <FField label="Calle">
                    <Input placeholder="Av. Revolución 123" {...inputStyle} {...register('Calle', { required: true })} />
                  </FField>
                </Stack>
                <Stack gap="16px">
                  <FField label="Número de Vivienda">
                    <Input placeholder="Depto 2B" {...inputStyle} {...register('NVivienda', { required: true })} />
                  </FField>
                  <FField label="Colonia">
                    <Input placeholder="Centro" {...inputStyle} {...register('Colonia', { required: true })} />
                  </FField>
                  <FField label="Referencias">
                    <Input placeholder="Frente al parque" {...inputStyle} {...register('Referencias')} />
                  </FField>
                  <FField label="Código Postal">
                    <Input type="number" placeholder="44100" {...inputStyle} {...register('CodigoPostal', { required: true })} />
                  </FField>
                  <FField label="Fecha de Nacimiento">
                    <Input type="date" {...inputStyle} {...register('Nacimiento', { required: true })} />
                  </FField>
                  <FField label="Género">
                    <Select {...inputStyle} {...register('Genero', { required: true })}>
                      <option value=""> </option>
                      {generoOpts.map((g) => (
                        <option key={g.id} value={g.id}>{g.valor}</option>
                      ))}
                    </Select>
                  </FField>
                  <FField label="CURP">
                    <Input placeholder="TOEE931129HJCRRM09" {...inputStyle} {...register('CURP')} />
                  </FField>
                  <FField label="Sección Electoral">
                    <AutoComplete openOnFocus onChange={(_k: any, vals: any) => setCandi(JSON.stringify(vals))}>
                      <AutoCompleteInput variant="filled" borderRadius="lg" placeholder="Selecciona una Sección" required
                        _focus={{ bg: 'white', borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                      />
                      <AutoCompleteList maxH="300px" overflowY="auto">
                        {secciones.map((s: any) => (
                          <AutoCompleteItem key={`sec-${s.id}`} label={`${s.Municipio} - ${s.Seccion} (${s.Tipo})`} value={s.id}>
                            <Box>
                              <Text fontWeight="bold">{s.Municipio}</Text>
                              <Text fontSize="sm" color="gray.500">Sección {s.Seccion} - {s.Tipo}</Text>
                            </Box>
                          </AutoCompleteItem>
                        ))}
                      </AutoCompleteList>
                    </AutoComplete>
                  </FField>
                </Stack>
              </SimpleGrid>

              <Box mt="20px">
                <FField label="Fotografía INE">
                  <HStack>
                    <Button
                      size="sm"
                      variant="outline"
                      borderRadius="lg"
                      leftIcon={<Icon as={MdUpload} h="16px" w="16px" />}
                      onClick={() => inputFileRef.current?.click()}
                    >
                      Subir archivo
                    </Button>
                    <Text fontSize="sm" color="gray.500">
                      {textFileEvidencia || 'Ningún archivo seleccionado'}
                    </Text>
                  </HStack>
                  <input
                    ref={inputFileRef}
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </FField>
              </Box>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3} px={6} py={4}>
              <Button variant="ghost" borderRadius="xl" onClick={onCloseNuevo}>Cancelar</Button>
              <Button colorScheme="brand" type="submit" isLoading={isSubmitting} borderRadius="xl" px={8}>Guardar</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* ── Table ─────────────────────────────────────────── */}
      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>
          {table.getHeaderGroups().map((hg) => (
            <Tr key={hg.id}>
              {hg.headers.map((header) => (
                <Th pe="10px" borderColor={borderColor} key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder ? null : (
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color="gray.400"
                      cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Flex>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.length === 0 ? (
            <Tr>
              <Td colSpan={columns.length} textAlign="center" py="40px" color="gray.400">
                No se encontraron registros con los filtros actuales
              </Td>
            </Tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Tr key={row.id} _hover={{ bg: rowHover }} transition="background 0.1s">
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    fontSize={{ sm: '14px' }}
                    minW={{ sm: '120px', md: 'auto' }}
                    borderColor={borderColor}
                    py="12px"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>

      {/* ── Pagination ────────────────────────────────────── */}
      <Flex w="100%" justify="space-between" px="20px" pt="10px" pb="5px">
        <Text fontSize="sm" color="gray.500" fontWeight="normal" mb={{ sm: '24px', md: '0px' }}>
          Mostrando{' '}
          {filteredData.length === 0
            ? '0'
            : `${pageSize * pageIndex + 1}–${Math.min(pageSize * (pageIndex + 1), filteredData.length)}`}{' '}
          de {filteredData.length} registros
        </Text>
        <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
          <Button
            variant="no-effects"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            w="40px" h="40px" borderRadius="50%" bg="transparent"
            border="1px solid" borderColor={borderColor}
            display={table.getCanPreviousPage() ? 'flex' : 'none'}
            _hover={{ bg: 'whiteAlpha.100', opacity: '0.7' }}
          >
            <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
          </Button>
          {createPages(table.getPageCount()).map((p, i) => (
            <Button
              key={i}
              variant="no-effects"
              onClick={() => table.setPageIndex(p - 1)}
              w="40px" h="40px" borderRadius="50%"
              bg={p === pageIndex + 1 ? brandColor : 'transparent'}
              border={p === pageIndex + 1 ? 'none' : '1px solid lightgray'}
              _hover={p === pageIndex + 1 ? { opacity: '0.7' } : { bg: 'whiteAlpha.100' }}
            >
              <Text fontSize="sm" color={p === pageIndex + 1 ? '#fff' : textColor}>{p}</Text>
            </Button>
          ))}
          <Button
            variant="no-effects"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            w="40px" h="40px" borderRadius="50%" bg="transparent"
            border="1px solid" borderColor={borderColor}
            display={table.getCanNextPage() ? 'flex' : 'none'}
            _hover={{ bg: 'whiteAlpha.100', opacity: '0.7' }}
          >
            <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
          </Button>
        </Stack>
      </Flex>
    </Flex>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.400" mb="2px">{label}</Text>
      <Text fontSize="sm" fontWeight="500">{value}</Text>
    </Box>
  );
}

function FField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <FormControl>
      <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.500" mb={1}>{label}</FormLabel>
      {children}
    </FormControl>
  );
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => { setValue(initialValue); }, [initialValue]);
  React.useEffect(() => {
    const t = setTimeout(() => onChange(value), debounce);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <SearchBar
      {...props}
      value={value}
      onChange={(e: any) => setValue(e.target.value)}
      h="44px"
      w={{ lg: '390px' }}
      borderRadius="16px"
    />
  );
}
